import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from '../components/notifications';
import sessionManager from '../services/SessionManager';
import { apiService } from '../services/api';
import { joinSessionAndNavigate } from '../services/sessionJoinActions';
import { SESSION_STATUS } from '../constants';
import { countActiveParticipants, stripParticipantImages } from '../utils/participantUtils';
import { useSessionManager } from './useSessionManager';
import browserCacheService from '../services/BrowserCacheService';
import pollingManager from '../services/PollingManager';
import { useConfirm } from '../components/ui/ConfirmProvider.jsx';

const isDev = process.env.NODE_ENV === 'development';
const debugLog = isDev ? console.log.bind(console) : () => { };

// Cache-Key pro Schule UND User (Backend filtert Session-Sichtbarkeit user-spezifisch)
const buildSessionsCacheKey = (authUser) =>
  authUser?.schoolId && authUser?.id ? `sessions_main_${authUser.schoolId}_${authUser.id}` : null;

// Identity-Guard: verhindert setSessions mit neuen Objekt-Identitaeten,
// wenn sich inhaltlich nichts geaendert hat (sonst Re-Render von
// MainScreen + Header bei jeder Rueckkehr zur Home-View und jedem Poll)
const sessionsEqual = (a, b) => (
  a.length === b.length
  && a.every((s, i) => (
    s.id === b[i].id
    && s.status === b[i].status
    && s.currentParticipants === b[i].currentParticipants
    && s.startTime === b[i].startTime
  ))
);

export const useMainScreenSessions = ({ authUser, navigate, setCurrentView }) => {
  const confirm = useConfirm();
  const { activeSession: smSession, checkServerSession } = useSessionManager();
  const [activeSession, setActiveSession] = useState(null);
  const activeSessionRef = useRef(null);
  const [dismissedSessionId, setDismissedSessionId] = useState(null);
  // Stale-While-Revalidate: gecachte Sessions sofort anzeigen, fetchSessions()
  // läuft beim Mount trotzdem und aktualisiert still (Polling bleibt unverändert)
  const [sessions, setSessions] = useState(() => {
    const cacheKey = buildSessionsCacheKey(authUser);
    return (cacheKey && browserCacheService.get(cacheKey)) || [];
  });
  const [hasSessionErrors, setHasSessionErrors] = useState(false);
  // Fetch-Fehler getrennt vom Session-State: bestehende (stale) Sessions bleiben
  // sichtbar, MainScreen zeigt ein Retry-Banner statt einer leeren Liste
  const [sessionsError, setSessionsError] = useState(null);

  // Ref synchron halten fuer Side-Effect-freien Zugriff
  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  // Reset dismissedSessionId wenn sich die aktive Session aendert
  useEffect(() => {
    if (activeSession && activeSession.id !== dismissedSessionId) {
      setDismissedSessionId(null);
    }
  }, [activeSession?.id, dismissedSessionId]);

  // SessionManager-Events -> lokaler State synchronisieren
  useEffect(() => {
    if (smSession) {
      setActiveSession(smSession);
    } else if (activeSessionRef.current) {
      if (activeSessionRef.current.status === SESSION_STATUS.ACTIVE) {
        toast.info('Deine Session wurde automatisch beendet.');
      }
      setActiveSession(null);
    }
  }, [smSession]);

  // Memoized session filters
  const activeSessions = useMemo(
    () => sessions.filter(s => s.status === SESSION_STATUS.ACTIVE),
    [sessions]
  );
  const plannedSessions = useMemo(
    () => sessions.filter(s => s.status === SESSION_STATUS.PLANNED),
    [sessions]
  );
  const completedSessions = useMemo(
    () => sessions.filter(s => s.status === SESSION_STATUS.COMPLETED),
    [sessions]
  );

  const transformSession = useCallback((session) => ({
    id: session.id,
    topic: session.topic || 'Unbekanntes Thema',
    location: session.location || 'Online',
    status: session.status || 'PLANNED',
    startTime: session.startTime,
    endTime: session.endTime,
    hostId: session.hostId,
    // 🔒 DSGVO: Anzeigename statt E-Mail (das Backend liefert die Host-E-Mail nur
    // noch an den Host selbst und an Schulpersonal aus).
    hostName: session.tenant_users?.tenant_profiles?.displayName || 'Unbekannt',
    maxParticipants: session.maxParticipants || 4,
    participants: session.participants || session.tenant_session_participants || [],
    currentParticipants: countActiveParticipants(session.participants || session.tenant_session_participants),
    type: session.type || 'PUBLIC',
    description: session.description || '',
    verificationCode: session.verificationCode,
    // Nur fuer Host und aktive Teilnehmende geliefert (siehe GET /sessions)
    learningGoals: session.learningGoals || [],
  }), []);

  const fetchSessions = useCallback(async () => {
    debugLog(`\uD83D\uDCC5 Lade Sessions fuer Schule: ${authUser?.schoolId}`);

    try {
      // Explizit ACTIVE und PLANNED separat laden, damit keine abgeschlossenen Sessions
      // die Pagination verdrängen (Standard-Limit 10, sortiert nach createdAt desc).
      const [activeResult, plannedResult] = await Promise.all([
        apiService.getSessions({ status: 'ACTIVE', limit: 50 }),
        apiService.getSessions({ status: 'PLANNED', limit: 50 }),
      ]);

      const activeArray = activeResult?.data || activeResult?.sessions || [];
      const plannedArray = plannedResult?.data || plannedResult?.sessions || [];
      const combined = [...activeArray, ...plannedArray];

      debugLog('\u2705 Sessions geladen:', combined.length);

      const transformed = stripParticipantImages(combined.map(transformSession));
      setSessions(prev => (sessionsEqual(prev, transformed) ? prev : transformed));
      setSessionsError(null);
      debugLog('\uD83D\uDCBE Sessions gespeichert:', transformed.length);

      const cacheKey = buildSessionsCacheKey(authUser);
      if (cacheKey) {
        browserCacheService.set(cacheKey, transformed, browserCacheService.TTL.SESSIONS);
      }
    } catch (fetchError) {
      console.error('\u274C FEHLER beim Laden der Sessions:', fetchError);
      // Stale-on-Error: Liste NICHT leeren - eine geleerte Liste sah f\u00FCr User
      // wie "alle Sessions weg" aus, obwohl nur der Fetch fehlgeschlagen ist
      setSessionsError(fetchError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.schoolId, authUser?.id, transformSession]);

  // Lade Sessions beim Start
  useEffect(() => {
    if (authUser?.schoolId) {
      // Falls der User erst nach dem Mount geladen wurde: gecachte Sessions
      // sofort anzeigen, der parallele Fetch aktualisiert still
      const cacheKey = buildSessionsCacheKey(authUser);
      const cached = cacheKey ? browserCacheService.get(cacheKey) : null;
      if (cached) {
        setSessions(prev => (prev.length === 0 ? cached : prev));
      }
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.schoolId, fetchSessions]);

  // Periodisch auffrischen (alle 5 Minuten), damit remote beendete Sessions
  // verschwinden. PollingManager statt setInterval: pausiert bei verstecktem
  // Tab und verlängert das Intervall bei Inaktivität (Akku/Netz).
  useEffect(() => {
    if (!authUser?.schoolId) return undefined;
    pollingManager.register('mainscreen-sessions', fetchSessions, {
      interval: 5 * 60 * 1000,
      immediate: false, // Mount-Fetch läuft bereits separat
    });
    return () => pollingManager.unregister('mainscreen-sessions');
  }, [authUser?.schoolId, fetchSessions]);

  // Einmaliger Server-Check (Recovery fuer vergessene Sessions)
  useEffect(() => {
    if (authUser?.id) {
      checkServerSession();
    }
  }, [authUser?.id, checkServerSession]);

  // Session Error Check
  useEffect(() => {
    const checkForSessionErrors = async () => {
      try {
        if (activeSession) return;

        const sessionData = localStorage.getItem('currentSession');
        if (!sessionData) return;

        const isValid = await sessionManager.refreshSessionStatus();
        setHasSessionErrors(!isValid);
      } catch (checkError) {
        console.error('Fehler bei der Session-Validierung:', checkError);
        setHasSessionErrors(true);
      }
    };

    checkForSessionErrors();
  }, [activeSession]);

  const handleJoinSession = useCallback(async (sessionId, code) => {
    try {
      // Gemeinsamer Join-Flow: Toast (nach participantStatus), justJoined-Overlay, Navigation
      await joinSessionAndNavigate(sessionId, navigate, { code, joinContext: code ? 'CODE' : 'OPEN_LIST' });
    } catch (joinError) {
      // Fehler-Toast kommt bereits aus joinSessionAndNavigate - aber re-throwen:
      // Caller (z.B. SessionCard) zeigen ihre Bestaetigung ("Platz reserviert!")
      // nur, wenn der Join wirklich durchging. Vorher wurde der Fehler hier
      // geschluckt und ein Gekickter sah Fehler- UND Erfolgs-Toast zugleich.
      console.error('Fehler beim Beitreten:', joinError);
      throw joinError;
    }
  }, [navigate]);

  const handleStartSession = useCallback(async (sessionData) => {
    try {
      const newSession = await sessionManager.createSession({
        ...sessionData,
        hostId: authUser.id
      });

      setActiveSession(newSession);
      setSessions(prev => [...prev, newSession]);

      if (newSession.status === SESSION_STATUS.ACTIVE) {
        sessionManager.startFocusTracking(newSession.id);
      }

      debugLog('Neue Session erstellt:', newSession);
      setCurrentView('session');
    } catch (startError) {
      console.error('Fehler beim Erstellen der Session:', startError.message);
    }
  }, [authUser?.id, setCurrentView]);

  const handleEndSession = useCallback(async (action, sessionData) => {
    debugLog('HandleEndSession called:', action, sessionData);

    if (!sessionManager) {
      console.error('SessionManager ist nicht verfuegbar');
      return;
    }

    switch (action) {
      case 'update': {
        if (!sessionData || !sessionData.participants) {
          console.error('Ungueltige Session-Daten:', sessionData);
          return;
        }

        const updatedSession = {
          ...sessionData,
          participants: Array.from(
            new Map(sessionData.participants.map(p => [p.id, { ...p }])).values()
          )
        };

        setActiveSession(updatedSession);
        setSessions(prev => prev.map(s =>
          s.id === updatedSession.id ? updatedSession : s
        ));
        break;
      }

      case 'complete':
        if (!activeSession) {
          console.error('Keine aktive Session zum Beenden vorhanden');
          return;
        }

        if ((await confirm({ title: 'Beenden?', text: 'Möchtest du die Session wirklich beenden?', variant: 'danger', confirmLabel: 'Beenden' }))) {
          try {
            await sessionManager.completeSession(activeSession.id);
          } catch (completeError) {
            console.error('Fehler beim Beenden der Session:', completeError);
            toast.error('Session konnte nicht beendet werden');
            return;
          }

          setSessions(prev => prev.map(s =>
            s.id === activeSession.id ? { ...s, status: SESSION_STATUS.COMPLETED, endTime: new Date().toISOString() } : s
          ));
          setActiveSession(null);
          setCurrentView('home');
          // Server-Zustand bestätigen: beendete Session aus Liste entfernen
          fetchSessions();
        }
        break;

      case 'cancel':
        if (!activeSession) {
          console.error('Keine aktive Session zum Abbrechen vorhanden');
          return;
        }

        if ((await confirm({ title: 'Session abbrechen?', text: 'Möchtest du die Session wirklich abbrechen?', variant: 'danger', confirmLabel: 'Session abbrechen', cancelLabel: 'Zurück' }))) {
          sessionManager.stopFocusTracking();

          // Bis Sep 2026 wurde hier nur lokal gefiltert - der Server erfuhr nichts,
          // die Session blieb ACTIVE und war nach dem naechsten fetchSessions() zurueck.
          try {
            await sessionManager.cancelSession(activeSession.id);
          } catch (cancelError) {
            console.error('Fehler beim Abbrechen der Session:', cancelError);
            toast.error('Session konnte nicht abgebrochen werden');
            return;
          }

          browserCacheService.invalidateSessions();
          setSessions(prev => prev.filter(s => s.id !== activeSession.id));
          setActiveSession(null);
          setCurrentView('home');
          // Server-Zustand bestaetigen
          fetchSessions();
        }
        break;

      case 'leave':
        if (!activeSession || !activeSession.participants) {
          console.error('Keine aktive Session oder Teilnehmerliste zum Verlassen vorhanden');
          return;
        }

        if ((await confirm({ title: 'Verlassen?', text: 'Möchtest du die Session wirklich verlassen?', variant: 'danger', confirmLabel: 'Verlassen' }))) {
          sessionManager.stopFocusTracking();

          try {
            sessionManager.leaveSession(activeSession.id);
          } catch (leaveError) {
            console.error('Fehler beim Verlassen der Session:', leaveError);
          }

          const sessionAfterLeave = {
            ...activeSession,
            participants: activeSession.participants.filter(p => String(p.id) !== String(authUser?.id))
          };

          if (sessionAfterLeave.participants.length === 0) {
            setSessions(prev => prev.filter(s => s.id !== activeSession.id));
          } else {
            setSessions(prev => prev.map(s =>
              s.id === sessionAfterLeave.id ? sessionAfterLeave : s
            ));
          }
          setActiveSession(null);
          setCurrentView('home');
          fetchSessions();
        }
        break;

      default:
        console.warn('Unknown session action:', action);
    }
  }, [activeSession, authUser?.id, setCurrentView, fetchSessions, confirm]);

  const cleanupInvalidSessions = useCallback(() => {
    try {
      debugLog('Versuche, ungueltige Sessions zu bereinigen...');
      const success = sessionManager.removeInvalidSession();
      return success;
    } catch (cleanupError) {
      console.error('Fehler beim Bereinigen der Sessions:', cleanupError);
      toast.error('Fehler beim Zurücksetzen der Session', {
        position: "top-right",
        autoClose: 3000
      });
      return false;
    }
  }, []);

  return {
    activeSession,
    sessions,
    activeSessions,
    plannedSessions,
    completedSessions,
    dismissedSessionId,
    hasSessionErrors,
    sessionsError,
    activeSessionRef,
    handleJoinSession,
    handleStartSession,
    handleEndSession,
    cleanupInvalidSessions,
    setDismissedSessionId,
    fetchSessions
  };
};
